import {useEffect, useRef, useState} from 'react';
import Header from './Header';
import Footer from './Footer';
import GettingStartedActions from './GettingStartedActions';
import {HeroPromises} from './sections/HeroSection';
import {trackEvent, EVENTS} from './utils/analytics';
import WhatChanged from './sections/WhatChanged';
import PlatformFeatures from './sections/PlatformFeatures';
import MCPRevolutionSection from './sections/MCPRevolutionSection';
import WinTogether from './sections/WinTogether';
import StrategyManifesto from './sections/StrategyManifesto';
import TrustSection from './sections/TrustSection';
import CTASection from './sections/CTASection';
import {CopyableCommand} from '../client/ClientInstallInstructions';
import {productLifecycle} from './productLifecycle';
import PlatformCapability from './PlatformCapability';
import './platform.css';

function ProductLoop() {
  const diagram = useRef(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    let visible = false;
    const update = () => setPlaying(visible && !document.hidden);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      update();
    }, {threshold: .25});
    observer.observe(diagram.current);
    document.addEventListener('visibilitychange', update);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', update);
    };
  }, []);
  return <nav ref={diagram} className="product-loop" data-playing={playing} aria-label="Explore the Pabs product lifecycle">
    <svg className="product-loop-ring" viewBox="0 0 100 100" fill="none" aria-hidden="true">
      <circle className="product-loop-line" cx="50" cy="50" r="48"/>
      <g className="product-loop-signal">
        <path className="product-loop-arc" d="M95.65 35.17 A48 48 0 0 1 98 50"/>
        <circle className="product-loop-dot" cx="98" cy="50" r="1.2"/>
      </g>
    </svg>
    <div className="product-loop-center"><span>Pabs</span><p>Orchestrating<br/>your agents.</p></div>
    <ol>{productLifecycle.map(stage => <li key={stage.id} style={{left: `${stage.position[0]}%`, top: `${stage.position[1]}%`}}>
      <a href={`#${stage.id}`}><strong>{stage.label}</strong><span>{stage.contribution}</span></a>
    </li>)}</ol>
  </nav>;
}

export default function HomepageNew() {
  const [active, setActive] = useState('shape');
  const stageNavigation = useRef(null);
  useEffect(() => {
    trackEvent(EVENTS.HOMEPAGE_VIEW);
    const destination = document.getElementById(window.location.hash.slice(1));
    if (destination) destination.scrollIntoView();
    else window.scrollTo(0, 0);
    // Evaluate all phases at the same activation line, in either scroll direction.
    // IntersectionObserver's changed entries are not a complete visibility ranking.
    let frame;
    const update = () => {
      const navigation = stageNavigation.current;
      if (!navigation) return;
      const line = parseFloat(getComputedStyle(navigation).top) + navigation.offsetHeight + 24;
      let current = productLifecycle[0].id;
      for (const stage of productLifecycle) {
        if (document.getElementById(stage.id)?.getBoundingClientRect().top <= line) current = stage.id;
      }
      setActive(current);
    };
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', scheduleUpdate, {passive: true});
    window.addEventListener('resize', scheduleUpdate);
    // Earlier feature sections change height as their images/fonts load.
    const resize = new ResizeObserver(scheduleUpdate);
    resize.observe(document.querySelector('.pabs-platform main'));
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, []);
  return <div className="pabs-platform min-h-screen bg-brand-900 font-inter text-brand-50">
    <Header/>
    <main>
      <section className="platform-hero">
        <div className="platform-hero-copy">
          <h1>Product and design leadership for everything you build</h1>
          <p>Pabs guides your agents, challenges what gets built and reviews what comes back, keeping your strategy alive as you learn.</p>
          <GettingStartedActions showHow={false}/>
        </div>
        <ProductLoop/>
      </section>
      <div className="platform-promises"><HeroPromises/></div>
      <div className="platform-workflow" id="platform">
      <div className="platform-stage-navigation" ref={stageNavigation}>
      <nav className="platform-stage-nav" aria-label="Product lifecycle sections">
        {productLifecycle.map(stage => <a key={stage.id} href={`#${stage.id}`} aria-current={active === stage.id ? 'step' : undefined}>{stage.label}</a>)}
      </nav>
      </div>
      <div className="platform-workflow-intro">
        <h2>Start with the work in front of you.</h2>
        <p>Pick the task you need help with. Copy a request into your connected AI tool, or ask in Pabs. The highlighted names link to the feature, skill or MCP tool behind each action.</p>
      </div>
      {productLifecycle.map(stage => <section id={stage.id} key={stage.id} className="platform-stage">
        <h2>{stage.title}</h2>
        <div className="platform-stage-overview">
          <p>{stage.body}</p>
          <ul className="platform-stage-actions">
            {stage.actions.map(action => <li key={action.lead}>
              <strong>{action.lead}</strong> using <PlatformCapability {...action.capability}/>. {action.outcome}
            </li>)}
          </ul>
          <div className="platform-task">
            <h3>Try asking</h3>
            <div className="pabs-install" data-appearance="dark"><CopyableCommand value={stage.prompt} label={`Copy ${stage.label.toLowerCase()} prompt`}/></div>
          </div>
        </div>
        <PlatformFeatures phase={stage.id}/>
      </section>)}
      </div>
      <WhatChanged/>
      <StrategyManifesto/>
      <MCPRevolutionSection/>
      <WinTogether/>
      <TrustSection/>
      <CTASection audience="solo_founder"/>
    </main>
    <Footer/>
  </div>;
}
